'use client';

import React, { useEffect, useState } from 'react';

import IMAGE_SIZE from '../../constants/image-size.constants';
import { OPERATORS } from '../../constants/constants';
import { useOptions, useOptionsDispatch } from '@/providers/options';
import { WRAPPER_CLASS_NAMES } from '@/constants/ads.constants';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import { paginateArticles } from '@/utils/articles/client';
import SummaryArticleCard from '@/components/ArticleCards/SummaryCard';
import FilterBar from '../../components/Select/FilterBar';
import { Badge, CircularProgress, IconButton } from '@mui/material';
import FilterDrawer from '@/components/Select/FilterDrawer';
import { FilterAltSharp } from '@mui/icons-material';
import { EMPTY_FILTER } from '@/constants/article.constants';
import type { TypeArticleWithoutUnresolvableLinksResponse } from '@/contentful/types';

type CategoryProps = {
  articles?: TypeArticleWithoutUnresolvableLinksResponse[];
  pageTitle: string;
  contentPillar?: string;
  pillarType?: string;
  pillars: ContentPillar[];
  sectors: ContentPillar[];
  locations: ContentPillar[];
  contentCategories: ContentPillar[];
  topStory?: boolean;
  memberContent?: boolean;
};

const PAGE_SIZE = 12;

let initialRender = true;
const Category = ({
  pageTitle,
  articles: initialArticles,
  contentPillar,
  pillarType,
  pillars,
  sectors,
  locations,
  contentCategories,
  topStory,
  memberContent
}: CategoryProps) => {
  const options = useOptions();
  const setOptions = useOptionsDispatch();
  const hasInitialArticles =
    typeof initialArticles !== 'undefined' &&
    initialArticles &&
    initialArticles.length > 0;

  const [loading, setLoading] = useState<boolean>(!hasInitialArticles);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [articles, setArticles] = useState<
    TypeArticleWithoutUnresolvableLinksResponse[]
  >(hasInitialArticles ? initialArticles : []);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [foundAll, setFoundAll] = useState<boolean>(false);
  const [filter, setFilter] = useState<ArticleFilter>(
    pillarType
      ? {
          ...EMPTY_FILTER,
          [pillarType]: [contentPillar]
          // pillarFilter: [contentPillar]
        }
      : EMPTY_FILTER
  );
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const [categoryOperator, setCategoryOperator] = useState<string>(
    OPERATORS.AND
  );

  const handleFilterChange = ({
    newFilter,
    operator
  }: {
    newFilter: ArticleFilter;
    operator: string;
  }) => {
    setFilter(newFilter);
    setCategoryOperator(operator);
    setCurrentPage(0);
    setFoundAll(false);
  };

  const loadMoreArticles = async () => {
    // setLoading(true);
    setLoadingMore(true);
    if (!foundAll) {
      const query: FilterQuery = {
        start: currentPage * PAGE_SIZE,
        rows: PAGE_SIZE,
        categoryOperator,
        topStory,
        memberContent
      };

      if (filter.pillar && filter.pillar.length > 0) {
        query.pillar = filter.pillar.join(',');
      }

      if (filter.sector && filter.sector.length > 0) {
        query.sector = filter.sector.join(',');
      }

      if (filter.location && filter.location.length > 0) {
        query.locationFilter = filter.location.join(',');
      }

      if (filter.contentCategory && filter.contentCategory.length > 0) {
        query.category = filter.contentCategory.join(',');
      }

      try {
        const newArticles = await paginateArticles(query);
        // setFoundAll(false);
        if (newArticles.length < PAGE_SIZE) {
          setFoundAll(true);
        }
        setArticles(articles.concat(newArticles));
        setCurrentPage(currentPage + 1);
      } catch (err) {
        console.log(err);
      }
      // setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const getArticles = async () => {
      if (initialRender && hasInitialArticles) {
        initialRender = false;
        return;
      }
      setLoading(true);
      window && window.scrollTo(0, 0);
      setCurrentPage(1);

      const query: FilterQuery = pillarType
        ? {
            start: 0,
            rows: PAGE_SIZE,
            categoryOperator,
            topStory,
            memberContent
          }
        : {
            start: 0,
            rows: PAGE_SIZE,
            categoryOperator,
            topStory,
            memberContent
          };

      if (filter.pillar && filter.pillar.length > 0) {
        query.pillar = filter.pillar.join(',');
      }

      if (filter.sector && filter.sector.length > 0) {
        query.sector = filter.sector.join(',');
      }

      if (filter.location && filter.location.length > 0) {
        query.locationFilter = filter.location.join(',');
      }

      if (filter.contentCategory && filter.contentCategory.length > 0) {
        query.category = filter.contentCategory.join(',');
      }

      try {
        const articlesResponse = await paginateArticles(query);
        setFoundAll(false);
        if (
          articlesResponse.length < 1 ||
          articlesResponse.length < PAGE_SIZE
        ) {
          setFoundAll(true);
        }
        setArticles(articlesResponse);
      } catch (err) {
        console.log(err);
      }
      setLoading(false);
    };

    void getArticles();
  }, [
    filter,
    categoryOperator,
    hasInitialArticles,
    pillarType,
    topStory,
    memberContent
  ]);

  const filtered =
    filter.contentCategory.length > 0 ||
    filter.location.length > 0 ||
    filter.sector.length > 0 ||
    filter.pillar.length > 0;

  return (
    <div className='theme-container category-container'>
      <FilterDrawer
        open={mobileFilterOpen}
        filter={filter}
        handleFilterChange={handleFilterChange}
        clearFilters={() => {
          setFilter(
            pillarType
              ? {
                  ...EMPTY_FILTER,
                  [pillarType]: [contentPillar]
                  // pillarFilter: [contentPillar]
                }
              : EMPTY_FILTER
          );
          setMobileFilterOpen(false);
        }}
        closeFilter={() => setMobileFilterOpen(false)}
        pillars={pillars}
        sectors={sectors}
        locations={locations}
        contentCategories={contentCategories}
      />
      <div className='standard-page-main'>
        <div className='standard-page__header category-header'>
          <div className='standard-page__title'>
            <h1
              style={{
                fontSize: '1em',
                lineHeight: '1.8',
                textTransform: 'uppercase',
                letterSpacing: '1.5px'
              }}
            >
              {pageTitle}
            </h1>
            <div className='standard-page__title-underline'>{}</div>
          </div>
          <div className='filter-mobile'>
            <Badge
              color='primary'
              overlap='circular'
              badgeContent={
                filtered
                  ? filter.contentCategory.length +
                    filter.location.length +
                    filter.sector.length +
                    filter.pillar.length
                  : 0
              }
            >
              <IconButton
                aria-label='Close'
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              >
                <FilterAltSharp sx={{ color: '#1c1c1c' }} />
              </IconButton>
            </Badge>
          </div>
        </div>
        <div className='standard-page__filter-bar'>
          <FilterBar
            filter={filter}
            handleFilterChange={handleFilterChange}
            clearFilters={() => {
              setFilter(
                pillarType
                  ? {
                      ...EMPTY_FILTER,
                      [pillarType]: [contentPillar]
                      // pillarFilter: [contentPillar]
                    }
                  : EMPTY_FILTER
              );
            }}
            pillars={pillars}
            sectors={sectors}
            locations={locations}
            contentCategories={contentCategories}
          />
        </div>
        <div className='category__body'>
          {loading ? (
            <div className='loading' style={{ gridColumn: '1/-1' }}>
              <div
                className='loading__inner'
                style={{
                  minHeight: '60vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress size={60} thickness={5} />
              </div>
            </div>
          ) : (
            articles.map((article) => (
              <SummaryArticleCard
                key={article.fields.slug}
                content={article}
                imageSize={IMAGE_SIZE.ARTICLE_LIST}
              />
            ))
          )}
        </div>
        {articles.length === 0 && !loading && (
          <div className='no-articles'>
            <h2>No articles found</h2>
          </div>
        )}
        {articles.length > 0 && articles.length < PAGE_SIZE && foundAll && (
          <div className='no-articles'></div>
        )}
        {!foundAll && articles.length > 0 && (
          <LoadMoreButton
            onClick={() => loadMoreArticles()}
            hasMore={!foundAll}
            loading={loadingMore}
          />
        )}
      </div>
    </div>
  );
};

export default Category;
